import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchCountriesStart, fetchCountriesSuccess, fetchCountriesFailure } from '../../../redux/slices/countriesSlice';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  TablePagination,
  Chip,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getCountries } from '../../../Action/api';

const Countries = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  
  const { data: countries, loading, error } = useSelector((state) => state.countries);
  
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    if (countries.length === 0) {
      fetchCountries();
    } else {
      setFilteredCountries(countries);
    }
  }, []);

  useEffect(() => {
    setFilteredCountries(countries);
  }, [countries]);

  const fetchCountries = async () => {
    try {
      dispatch(fetchCountriesStart());
      const data = await getCountries();
      dispatch(fetchCountriesSuccess(data));
    } catch (err) {
      dispatch(fetchCountriesFailure('Failed to fetch countries. Please try again.'));
      console.error('Error fetching countries:', err);
    }
  };

  useEffect(() => {
    let filtered = countries.filter((country) =>
      country.countryName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      country.countryCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      country.zoneName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      country.regionName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      country.territoryName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Apply sorting
    if (sortColumn) {
      filtered = [...filtered].sort((a, b) => {
        let aValue = a[sortColumn];
        let bValue = b[sortColumn];
        
        if (sortColumn === 'zoneName' || sortColumn === 'regionName' || sortColumn === 'territoryName') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
        
        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    setFilteredCountries(filtered);
    setPage(0);
  }, [searchTerm, countries, sortColumn, sortDirection]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedCountries = filteredCountries.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ 
      p: { xs: 1, sm: 2 }, 
      maxWidth: { xs: '100vw', md: 'calc(100vw - 280px)' }, 
      mx: 'auto',
      width: '100%'
    }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' }, 
        mb: 2,
        gap: { xs: 1, sm: 0 }
      }}>
        <Typography 
          variant={isMobile ? 'h6' : 'h5'} 
          component="h1" 
          sx={{ fontWeight: 600 }}
        >
          Countries Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />}
            size="small"
            onClick={fetchCountries}
            disabled={loading}
            sx={{ textTransform: 'none' }}
          >
            {!isMobile && 'Reload'}
          </Button>
          <Button 
            variant="contained" 
            startIcon={!isMobile && <AddIcon />}
            size="small"
            fullWidth={isMobile}
            sx={{ textTransform: 'none' }}
          >
            Add Country
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by country name, code, zone, region, or territory..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      ) : (
        <>
      <TableContainer 
        component={Paper} 
        sx={{ 
          boxShadow: 2,
          overflowX: 'auto',
          width: '100%',
          '&::-webkit-scrollbar': {
            height: '8px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#888',
            borderRadius: '4px',
          }
        }}
      >
        <Table size="small" sx={{ minWidth: { xs: 700, sm: 900 } }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>S.No</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Country Code</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Country Name</TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 700, 
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { backgroundColor: '#e0e0e0' }
                }}
                onClick={() => handleSort('zoneName')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Zone
                  {sortColumn === 'zoneName' && (
                    sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 700, 
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { backgroundColor: '#e0e0e0' }
                }}
                onClick={() => handleSort('regionName')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Region
                  {sortColumn === 'regionName' && (
                    sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 700, 
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { backgroundColor: '#e0e0e0' }
                }}
                onClick={() => handleSort('territoryName')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Territory
                  {sortColumn === 'territoryName' && (
                    sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedCountries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    No countries found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedCountries.map((country, index) => (
                <TableRow 
                  key={country.countryCode}
                  sx={{ 
                    '&:hover': { backgroundColor: '#f9f9f9' },
                    '&:nth-of-type(even)': { backgroundColor: '#fafafa' }
                  }}
                >
                  <TableCell sx={{ fontSize: '0.813rem' }}>{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell sx={{ fontSize: '0.813rem' }}>
                    <Chip label={country.countryCode} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.813rem', fontWeight: 500 }}>{country.countryName}</TableCell>
                  <TableCell sx={{ fontSize: '0.813rem' }}>
                    <Chip label={country.zoneName} size="small" color="primary" variant="outlined" />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.813rem' }}>{country.regionName}</TableCell>
                  <TableCell sx={{ fontSize: '0.813rem' }}>
                    <Chip label={country.territoryName} size="small" color="info" />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" color="primary">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component={Paper}
        sx={{ 
          mt: 1, 
          boxShadow: 1,
          '.MuiTablePagination-toolbar': {
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            justifyContent: 'center'
          },
          '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
            fontSize: { xs: '0.75rem', sm: '0.875rem' }
          }
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
        count={filteredCountries.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage={isMobile ? 'Rows:' : 'Rows per page:'}
      />
        </>
      )}
    </Box>
  );
};

export default Countries;
